import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Save, 
  ArrowLeft, 
  FileText, 
  Image as ImageIcon, 
  Tag, 
  User, 
  Clock, 
  Star, 
  TrendingUp, 
  Crown, 
  Globe,
  Calendar,
  CheckCircle,
  X,
  Layout,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Maximize2,
  Percent,
  Grid,
  Plus, Edit2, Trash2, Search, Check,
  List as ListIcon
} from 'lucide-react';
import toast from 'react-hot-toast';
import JoditEditor from 'jodit-react';

const backendUrl = 'https://justurbane.in/backend_php/public';

const AdminArticleEditorPage = () => {

  // --- Core State ---
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadMode, setUploadMode] = useState(false);
  const [fileToUpload, setFileToUpload] = useState(null);
  const navigate = useNavigate();
  const { articleId } = useParams();

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // --- Scheduling State ---
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleHour, setScheduleHour] = useState('');
  const [scheduleMinute, setScheduleMinute] = useState('');

  // --- Editor & Image Modal State ---
  const editor = useRef(null);
  const [content, setContent] = useState('');
  const [editorInstance, setEditorInstance] = useState(null);
  
  // Modal & Layout State
  const [modalConfig, setModalConfig] = useState({ isOpen: false, type: '', title: '' });
  const [selectedFiles, setSelectedFiles] = useState([]); 
  const [insertLayout, setInsertLayout] = useState({
    align: 'center', // 'left', 'center', 'right'
    width: 100,      // 20 to 100
    showCaption: false
  });

  // --- Author Management State ---
  const [showAuthorListModal, setShowAuthorListModal] = useState(false);
  const [authors, setAuthors] = useState([]);
  const [showAuthorModal, setShowAuthorModal] = useState(false);
  const [authorSearch, setAuthorSearch] = useState('');
  const [isEditingAuthor, setIsEditingAuthor] = useState(false);
  const [currentAuthor, setCurrentAuthor] = useState({
    id: null, name: '', display_name: '', email: '', bio: ''
  });

  // Fetch Authors
  const fetchAuthors = useCallback(async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${backendUrl}/api/admin/authors?search=${authorSearch}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setAuthors(data);
    } catch (err) {
      console.error('Failed to load authors', err);
    }
  }, [authorSearch]);

  useEffect(() => {
    fetchAuthors();
  }, [fetchAuthors]);

  // Handle Opening Modal
  const openAuthorModal = (author = null) => {
    if (author) {
      setIsEditingAuthor(true);
      setCurrentAuthor(author);
    } else {
      setIsEditingAuthor(false);
      setCurrentAuthor({ id: null, name: '', display_name: '', email: '', bio: '' });
    }
    setShowAuthorModal(true);
  };

  // Save Author
  const handleSaveAuthor = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const method = isEditingAuthor ? 'PUT' : 'POST';
      const url = isEditingAuthor 
        ? `${backendUrl}/api/admin/authors/${currentAuthor.id}`
        : `${backendUrl}/api/admin/authors`;

      // Auto-generate internal name if empty based on display name
      const payload = { ...currentAuthor };
      if (!payload.name) {
          payload.name = payload.display_name;
      }

      const res = await fetch(url, {
        method,
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const result = await res.json();

      if (!res.ok) {
        // Handle validation errors object
        if (result.messages) {
            const errorMsg = Object.values(result.messages).join(', ');
            throw new Error(errorMsg);
        }
        throw new Error(result.message || 'Failed to save author');
      }

      toast.success(isEditingAuthor ? 'Author Updated' : 'Author Created');
      setShowAuthorModal(false);
      fetchAuthors(); // Refresh list
      
      // If we just created a new author, auto-select them
      if (!isEditingAuthor) {
         setArticle(prev => ({ ...prev, author_name: currentAuthor.display_name }));
      }

    } catch (error) {
      toast.error(error.message);
    }
  };

  // Delete Author
  const handleDeleteAuthor = async (id) => {
    if(!window.confirm("Are you sure? This cannot be undone.")) return;
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${backendUrl}/api/admin/authors/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Author deleted');
        fetchAuthors();
        // If the deleted author was selected, clear it
        const deletedAuthor = authors.find(a => a.id === id);
        if (deletedAuthor && article.author_name === deletedAuthor.display_name) {
             setArticle(prev => ({ ...prev, author_name: '' }));
        }
      }
    } catch (error) {
      toast.error('Failed to delete author');
    }
  };

  // =========================================================================
  // 1. DATA FETCHING & INITIALIZATION
  // =========================================================================

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${backendUrl}/api/categories`);
        const data = await res.json();
        setCategories(data);
      } catch (err) {
        console.error('Failed to load categories', err);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    if (article?.category && categories.length) {
      const cat = categories.find(c => c.name === article.category);
      setSelectedCategory(cat);
    }
  }, [article, categories]);

  useEffect(() => {
    if (article?.body !== undefined) {
      setContent(article.body);
      setLoading(false);
    }
  }, [article?.body]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    if (articleId && articleId !== 'new') {
      fetchArticle();
    } else {
      const now = new Date();
      setArticle({
        id: null,
        title: '',
        body: '',
        summary: '',
        author_name: '',
        photographer: '',
        location: '',
        courtesy: '',
        category: 'fashion',
        subcategory: '',
        tags: [],
        featured: false,
        trending: false,
        premium: false,
        reading_time: 5,
        hero_image: '',
        status: 'draft'
      });
      
      setScheduleDate(now.toISOString().split('T')[0]);
      setLoading(false);
    }
  }, [navigate, articleId]);

  const fetchArticle = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${backendUrl}/api/admin/articles_1/${articleId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setArticle({
          ...data,
          tags: data.tags || []
        });

        if (data.scheduled_at) {
            const dateObj = new Date(data.scheduled_at);
            if (!isNaN(dateObj) && dateObj > new Date()) {
                setIsScheduled(true);
                setScheduleDate(dateObj.toISOString().split('T')[0]);
                setScheduleHour(String(dateObj.getHours()).padStart(2, '0'));
                setScheduleMinute(String(dateObj.getMinutes()).padStart(2, '0'));
            }
        } else {
            setScheduleDate(new Date().toISOString().split('T')[0]);
        }
      } else {
        throw new Error('Failed to fetch article');
      }
    } catch (error) {
      toast.error('Failed to load article');
      console.error('Article fetch error:', error);
      navigate('/admin/articles_1');
    } finally {
      setLoading(false);
    }
  };

  // =========================================================================
  // 2. HELPER FUNCTIONS
  // =========================================================================

  const getAvailableHours = (selectedDateStr) => {
    if (!selectedDateStr) return [];
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const isToday = selectedDateStr === now.toISOString().split('T')[0];
    
    const hours = [];
    let startHour = 0;

    if (isToday) {
      startHour = currentMinute >= 50 ? currentHour + 1 : currentHour;
    }

    for (let i = startHour; i < 24; i++) {
      hours.push(String(i).padStart(2, '0'));
    }
    return hours;
  };

  const getAvailableMinutes = (selectedDateStr, selectedHourStr) => {
    if (!selectedDateStr || !selectedHourStr) return [];
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const isToday = selectedDateStr === now.toISOString().split('T')[0];
    const isCurrentHour = parseInt(selectedHourStr) === currentHour;

    const allSlots = ['00', '10', '20', '30', '40', '50'];
    
    if (isToday && isCurrentHour) {
      return allSlots.filter(m => parseInt(m) > currentMinute);
    }
    return allSlots;
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  // =========================================================================
  // 3. EDITOR & MODAL LOGIC
  // =========================================================================

  const onEditorChange = useCallback((newContent) => {
    setContent(newContent);
  }, []);

  const onEditorBlur = useCallback((newContent) => {
    setArticle(prev => prev ? { ...prev, body: newContent } : null);
  }, []);

  const editorConfig = useMemo(() => ({
    readonly: false,
    toolbarSticky: false,
    buttons: [
      'bold', 'italic', 'underline', 'strikethrough', 'eraser', '|',
      'font', 'fontsize', '|',
      'align', 'outdent', 'indent', '|',
      'ul', 'ol', '|',
      'image', 'video', '|',
      'link', 'source', '|',
      'undo', 'redo'
    ],
    fontFamilies: [
      'Arial', 'Times New Roman', 'Courier New', 'Georgia',
      'Verdana', 'Tahoma', 'Palatino', 'Helvetica', 'Comic Sans MS'
    ],
    fontSizes: ['8', '10', '12', '14', '18', '24', '36', '48']
  }), []);

  const openModal = (type, title) => {
    if (editorInstance) {
      editorInstance.s.save(); 
    }

    setModalConfig({ isOpen: true, type, title });
    setSelectedFiles([]); 
    // Reset layout defaults
    setInsertLayout({ align: 'center', width: 100, showCaption: false });
  };

  const handleFileSelect = (e) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      const newFiles = filesArray.map(file => ({
        id: Math.random().toString(36).substr(2, 9),
        file,
        preview: URL.createObjectURL(file)
      }));

      if (modalConfig.type === 'collage') {
        const remainingSlots = 4 - selectedFiles.length;
        setSelectedFiles(prev => [...prev, ...newFiles.slice(0, remainingSlots)]);
      } else {
        setSelectedFiles(prev => [...prev, ...newFiles]);
      }
    }
  };

  const removeFile = (id) => {
    setSelectedFiles(prev => prev.filter(f => f.id !== id));
  };

  // --- CORE INSERT LOGIC WITH LAYOUT SUPPORT ---
  const handleInsertContent = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select at least one image');
      return;
    }

    if (modalConfig.type === 'collage' && selectedFiles.length !== 4) {
        toast.error('Collage requires exactly 4 images');
        return;
    }

    setSaving(true); 

    try {
        const images = await Promise.all(selectedFiles.map(item => fileToBase64(item.file)));
        
        // --- 1. Construct Layout Container Style ---
        let containerStyle = '';
        
        // Handle Alignment (Float/Margin)
        if (insertLayout.align === 'left') {
            containerStyle = `float: left; width: ${insertLayout.width}%; margin-right: 2rem; margin-bottom: 1.5rem; clear: left;`;
        } else if (insertLayout.align === 'right') {
            containerStyle = `float: right; width: ${insertLayout.width}%; margin-left: 2rem; margin-bottom: 1.5rem; clear: right;`;
        } else {
            // Center
            containerStyle = `width: ${insertLayout.width}%; margin: 2rem auto; display: block; clear: both;`;
        }

        let innerContent = '';

        // --- 2. Generate Content Based on Type ---
        if (modalConfig.type === 'carousel') {
          const slides = images.map(img => `
            <div class="snap-center shrink-0 w-80 h-96 bg-gray-100 rounded-xl overflow-hidden relative shadow-md border border-gray-100">
              <img src="${img}" style="width:100%; height:100%; object-fit:cover; display:block;" />
            </div>
          `).join('');
          innerContent = `
            <div style="position:relative; width:100%;">
                <div style="display:flex; gap:16px; overflow-x:auto; padding-bottom:16px; scroll-snap-type: x mandatory;">
                    ${slides}
                </div>
                <p style="text-align:center; color:#9ca3af; font-size:12px; margin-top:8px;">Swipe to see more</p>
            </div>
          `;
        } 
        else if (modalConfig.type === 'grid') {
          const items = images.map(img => `
            <div style="aspect-ratio:1/1; background-color:#f3f4f6; border-radius:12px; overflow:hidden; box-shadow:0 1px 2px rgba(0,0,0,0.05);">
              <img src="${img}" style="width:100%; height:100%; object-fit:cover; display:block;" />
            </div>
          `).join('');
          // Grid responsive logic inside style
          innerContent = `<div style="display:grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap:12px;">${items}</div>`;
        } 
        else if (modalConfig.type === 'collage') {
          const [i1, i2, i3, i4] = images;
          innerContent = `
            <div style="display:grid; grid-template-columns: repeat(4, 1fr); grid-template-rows: repeat(2, minmax(150px, auto)); gap:12px; aspect-ratio: 16/9;">
              <div style="grid-column: span 2; grid-row: span 2; border-radius:12px; overflow:hidden; position:relative;">
                <img src="${i1}" style="width:100%; height:100%; object-fit:cover; display:block;" />
              </div>
              <div style="grid-column: span 2; border-radius:12px; overflow:hidden; position:relative;">
                <img src="${i2}" style="width:100%; height:100%; object-fit:cover; display:block;" />
              </div>
              <div style="border-radius:12px; overflow:hidden; position:relative;">
                <img src="${i3}" style="width:100%; height:100%; object-fit:cover; display:block;" />
              </div>
              <div style="border-radius:12px; overflow:hidden; position:relative;">
                <img src="${i4}" style="width:100%; height:100%; object-fit:cover; display:block;" />
              </div>
            </div>
          `;
        }

        // --- 3. Wrap & Insert ---
        // We add a wrapper with the positioning styles
        // We add &nbsp; after to allow user to easily click out of the float flow
        const finalHtml = `
            <div class="article-media-wrapper" style="${containerStyle} box-sizing: border-box; position: relative; z-index: 10;">
                ${innerContent}
            </div>
            <p>&nbsp;</p>
        `;

        // Insert into editor
        if (editorInstance) {
          try {
            editorInstance.s.restore(); 
            
            editorInstance.s.insertHTML(finalHtml);
            
          } catch (e) {
            console.warn("Standard insert failed, forcing append", e);
            setContent(prev => prev + finalHtml);
          }
        } else {
           setContent(prev => prev + finalHtml);
        }

        setModalConfig({ ...modalConfig, isOpen: false });
        setSelectedFiles([]);
        toast.success('Layout inserted successfully!');

    } catch (error) {
        console.error(error);
        toast.error('Failed to process images');
    } finally {
        setSaving(false);
    }
  };

  // =========================================================================
  // 4. SAVE & UPLOAD HANDLERS
  // =========================================================================

  const handleSave = async () => {
    if (!article.title || !article.body || !article.author_name ) {
      toast.error('Please fill required fields');
      return;
    }

    if (isScheduled) {
        if (!scheduleDate || !scheduleHour || !scheduleMinute) {
            toast.error('Please select a valid date and time for scheduling');
            return;
        }
        const selectedTime = new Date(`${scheduleDate}T${scheduleHour}:${scheduleMinute}:00`);
        if (selectedTime <= new Date()) {
            toast.error('Scheduled time must be in the future');
            return;
        }
    }
    
    try {
      setSaving(true);
      const token = localStorage.getItem('admin_token');
      
      const formData = new FormData();
      formData.append('title', article.title);
      formData.append('body', article.body);
      formData.append('summary', article.summary || '');
      formData.append('author_name', article.author_name);
      formData.append('courtesy', article.courtesy);
      formData.append('photographer', article.photographer);
      formData.append('location', article.location);
      formData.append('category', article.category);
      formData.append('subcategory', article.subcategory || '');
      formData.append('tags', article.tags.join(','));
      formData.append('featured', article.featured.toString());
      formData.append('trending', article.trending.toString());
      formData.append('premium', article.premium.toString());
      formData.append('reading_time', article.reading_time.toString());
      formData.append('status', article.status || 'draft');
    
      if (article.hero_image instanceof File) {
        formData.append('hero_image', article.hero_image);
      }
    
      const url = article.id 
        ? `${backendUrl}/api/admin/articles_1/${article.id}`
        : `${backendUrl}/api/admin/articles_1`;
    
      const method = article.id ? 'POST' : 'POST'; 
      if (article.id) formData.append('_method', 'PUT'); 

      const saveResponse = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
    
      if (!saveResponse.ok) {
        const error = await saveResponse.json();
        throw new Error(error.detail || 'Save failed');
      }

      const savedResult = await saveResponse.json();
      const savedArticleId = savedResult.id || article.id;

      let scheduleMsg = '';
      if (isScheduled) {
          const scheduledDateTime = `${scheduleDate} ${scheduleHour}:${scheduleMinute}:00`;
          
          const scheduleResponse = await fetch(`${backendUrl}/api/admin/articles/schedule`, {
            method: 'POST',
            headers: { 
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                article_id: savedArticleId,
                scheduled_at: scheduledDateTime,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            })
          });

          const scheduleData = await scheduleResponse.json();
          if (scheduleResponse.ok && scheduleData.success) {
            scheduleMsg = ` & Scheduled for ${scheduleDate} ${scheduleHour}:${scheduleMinute}`;
          } else {
            toast.error(`Article saved, but scheduling failed: ${scheduleData.message}`);
          }
      }

      toast.success(article.id ? `Article Updated${scheduleMsg}!` : `Article Created${scheduleMsg}!`);
      
      if (!article.id) {
          navigate(`/admin/articles_1/edit/${savedArticleId}`);
      }

    } catch (error) {
      toast.error(error.message);
      console.error(error);
    } finally {
      setSaving(false);
    }
  };
    
  const handleFileUpload = async () => {
    if (!fileToUpload) {
      toast.error('Please select a file to upload');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('admin_token');
        
      const formData = new FormData();
      formData.append('title', article.title);
      formData.append('summary', article.summary);
      formData.append('author_name', article.author_name);
      formData.append('category', article.category);
      formData.append('content_file', fileToUpload);

      const response = await fetch(`${backendUrl}/api/admin/articles_1/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        toast.success('Article uploaded successfully!');
        navigate(`/admin/articles_1/edit/${result.article_id}`);
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to upload article');
    } finally {
      setSaving(false);
    }
  };

  const handleTagAdd = (tag) => {
    if (tag && !article.tags.includes(tag)) {
      setArticle({ ...article, tags: [...article.tags, tag] });
    }
  };

  const handleTagRemove = (tagToRemove) => {
    setArticle({ ...article, tags: article.tags.filter(tag => tag !== tagToRemove) });
  };

  // =========================================================================
  // 5. RENDER
  // =========================================================================
        
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading article editor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/articles_1')}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back</span>
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <h1 className="text-xl font-bold text-gray-900">
                {article?.id ? 'Edit Article' : 'Create New Article'}
              </h1>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={uploadMode ? handleFileUpload : handleSave}
                disabled={saving}
                className="flex items-center space-x-2 bg-amber-500 text-white px-6 py-2 rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50 shadow-sm"
              >
                <Save className="w-4 h-4" />
                <span>
                  {saving ? (
                    article?.id ? 'Updating...' : 'Saving...'
                  ) : 
                    article?.id ? 'Update' : 'Save'
                  }
                </span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Main Content Area */}
          <div className="lg:col-span-2 space-y-6">
            
            {uploadMode ? (
              <div className="bg-white rounded-xl shadow-sm border p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Upload Article File</h2>
                  <input
                      type="file"
                      accept=".rtf,.txt"
                      onChange={(e) => setFileToUpload(e.target.files[0])}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <button 
                    onClick={() => setUploadMode(false)}
                    className="mt-4 text-sm text-amber-600 hover:underline"
                  >
                    Switch to Manual Editor
                  </button>
              </div>
            ) : (
              <>
                {/* Title and Summary */}
                <div className="bg-white rounded-xl shadow-sm border p-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Article Title *
                      </label>
                      <input
                        type="text"
                        value={article?.title || ''}
                        onChange={(e) => setArticle({ ...article, title: e.target.value })}
                        className="w-full px-4 py-3 text-xl font-semibold border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                        placeholder="Enter article title..."
                      />
                    </div>
                      
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Summary *
                      </label>
                      <textarea
                        value={article?.summary || ''}
                        onChange={(e) => setArticle({ ...article, summary: e.target.value })}
                        rows={3}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                        placeholder="Brief article summary..."
                      />
                    </div>
                  </div>
                </div>

                {/* RICH CONTENT EDITOR & MODAL */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-0 overflow-hidden flex flex-col h-full min-h-[900px]">
                    
                    {/* Editor Header */}
                    <div className="flex items-center justify-between px-6 py-4 bg-white border-b border-gray-200">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-amber-50 rounded-lg border border-amber-100">
                          <FileText className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <h2 className="text-lg font-bold text-gray-900 leading-tight">Content Editor</h2>
                          <p className="text-xs text-gray-500 font-medium">Advanced layout & media controls</p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-3">
                        <div className="hidden xl:flex items-center space-x-1 mr-2 bg-white px-2 py-1.5 rounded-lg border border-gray-200 shadow-sm">
                          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-2">Layouts:</span>

                          <button
                            type="button"
                            onClick={() => openModal('carousel', 'Carousel')}
                            className="p-1.5 hover:bg-gray-50 text-gray-600 hover:text-amber-600 rounded-md transition-all flex items-center gap-1.5 group"
                            title="Insert Interactive Carousel"
                          >
                            <Globe className="w-3.5 h-3.5" />
                            <span className="text-xs font-medium">Carousel</span>
                          </button>

                          <div className="w-px h-4 bg-gray-200 mx-1"></div>

                          <button
                            type="button"
                            onClick={() => openModal('grid', 'Grid Gallery')}
                            className="p-1.5 hover:bg-gray-50 text-gray-600 hover:text-amber-600 rounded-md transition-all flex items-center gap-1.5 group"
                            title="Insert Grid Gallery"
                          >
                            <ImageIcon className="w-3.5 h-3.5" />
                            <span className="text-xs font-medium">Grid</span>
                          </button>

                          <div className="w-px h-4 bg-gray-200 mx-1"></div>

                          <button
                            type="button"
                            onClick={() => openModal('collage', 'Modern Collage')}
                            className="p-1.5 hover:bg-gray-50 text-gray-600 hover:text-amber-600 rounded-md transition-all flex items-center gap-1.5 group"
                            title="Insert Modern Collage"
                          >
                            <Crown className="w-3.5 h-3.5" />
                            <span className="text-xs font-medium">Collage</span>
                          </button>
                        </div>

                        <a
                          href="/admin/gallery"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all shadow-sm hover:shadow-md active:transform active:scale-95"
                        >
                          <ImageIcon className="w-4 h-4" />
                          <span className="text-sm font-semibold">Library</span>
                        </a>
                      </div>
                    </div>

                    {/* Jodit Instance */}
                    <div className="flex-1 relative bg-white">
                      <JoditEditor
                        ref={editor}
                        value={content}
                        config={editorConfig}
                        onBlur={onEditorBlur}
                        onChange={onEditorChange}
                        onInit={(instance) => setEditorInstance(instance)}
                        className="h-full min-h-[850px]"
                      />
                    </div>

                    {/* Editor Footer Status Bar */}
                    <div className="bg-gray-50 border-t border-gray-200 px-6 py-2.5 flex justify-between items-center text-xs text-gray-500 select-none">
                      <div className="flex items-center gap-6">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${saving ? 'bg-amber-400 animate-pulse' : 'bg-green-500'}`}></div>
                          <span className="font-medium">{saving ? 'Processing...' : 'Ready'}</span>
                        </div>
                        <div className="h-3 w-px bg-gray-300"></div>
                        <span>Words: <span className="font-mono font-semibold text-gray-700">{content ? content.split(/\s+/).filter(w => w.length > 0).length : 0}</span></span>
                        <div className="h-3 w-px bg-gray-300"></div>
                        <span>Reading time: <span className="font-mono font-semibold text-gray-700">~{Math.ceil((content ? content.split(/\s+/).length : 0) / 200)} min</span></span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-[10px] font-bold tracking-wide uppercase">Tailwind</span>
                        <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-[10px] font-bold tracking-wide uppercase">Responsive</span>
                      </div>
                    </div>

                    {/* UPDATED IMAGE SELECTION MODAL */}
                    {modalConfig.isOpen && (
                      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-md p-6">
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl overflow-hidden flex flex-col h-[85vh] animate-in fade-in zoom-in duration-200">
                          
                          {/* Modal Header */}
                          <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-white shrink-0">
                            <div>
                              <h3 className="text-xl font-bold text-gray-900">{modalConfig.title}</h3>
                              <p className="text-sm text-gray-500">Configure layout and select images</p>
                            </div>
                            <button
                              onClick={() => setModalConfig({ ...modalConfig, isOpen: false })}
                              className="text-gray-400 hover:text-gray-900 p-2 hover:bg-gray-100 rounded-full transition-colors"
                            >
                              <X className="w-6 h-6" />
                            </button>
                          </div>

                          {/* Modal Body: Split View */}
                          <div className="flex flex-1 overflow-hidden">
                            {/* Left: Image Selection (75%) */}
                            <div className="flex-1 overflow-y-auto p-6 custom-scrollbar bg-gray-50/50">
                                
                                {selectedFiles.length > 0 && (
                                  <div className="mb-6">
                                    <div className="flex items-center justify-between mb-3">
                                      <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                                        <ImageIcon className="w-4 h-4 text-amber-500" />
                                        Selected Media ({selectedFiles.length})
                                      </h4>
                                      {modalConfig.type === 'collage' && (
                                        <span className={`text-xs font-bold px-2 py-1 rounded ${selectedFiles.length === 4 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                          {selectedFiles.length}/4 Required
                                        </span>
                                      )}
                                    </div>

                                    <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                                      {selectedFiles.map((item, index) => (
                                        <div key={item.id} className="relative group aspect-square rounded-xl overflow-hidden border border-gray-200 shadow-sm bg-white cursor-move">
                                          <img
                                            src={item.preview}
                                            alt="Preview"
                                            className="w-full h-full object-cover"
                                          />
                                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                            <button
                                              onClick={() => removeFile(item.id)}
                                              className="p-2 bg-white text-red-500 rounded-full hover:scale-110 transition-transform shadow-lg"
                                            >
                                              <X className="w-4 h-4" />
                                            </button>
                                          </div>
                                          {modalConfig.type === 'collage' && (
                                            <div className="absolute top-2 left-2 px-2 py-1 bg-black/70 backdrop-blur-md rounded-md text-[10px] font-bold text-white shadow-sm">
                                              #{index + 1}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                      
                                      <label className="aspect-square rounded-xl border-2 border-dashed border-gray-300 hover:border-amber-500 hover:bg-amber-50/50 transition-all flex flex-col items-center justify-center cursor-pointer group">
                                        <input
                                          type="file"
                                          multiple
                                          accept="image/*"
                                          onChange={handleFileSelect}
                                          className="hidden"
                                        />
                                        <div className="w-10 h-10 rounded-full bg-gray-100 group-hover:bg-amber-100 flex items-center justify-center mb-2 transition-colors">
                                          <span className="text-gray-400 group-hover:text-amber-600 text-xl font-light">+</span>
                                        </div>
                                        <span className="text-xs font-medium text-gray-500 group-hover:text-amber-700">Add Image</span>
                                      </label>
                                    </div>
                                  </div>
                                )}

                                {selectedFiles.length === 0 && (
                                  <div className="h-full flex flex-col items-center justify-center text-center p-12 border-2 border-dashed border-gray-200 rounded-2xl hover:border-amber-400 transition-colors bg-white">
                                     <div className="w-16 h-16 bg-amber-50 text-amber-500 rounded-full flex items-center justify-center mb-4">
                                        <ImageIcon className="w-8 h-8" />
                                     </div>
                                     <h3 className="text-lg font-semibold text-gray-900">Drop images here</h3>
                                     <p className="text-sm text-gray-500 mt-1 mb-6">or click to browse from your computer</p>
                                     <label className="px-6 py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-lg cursor-pointer transition-all shadow-sm hover:shadow-md">
                                        Browse Files
                                        <input type="file" multiple accept="image/*" onChange={handleFileSelect} className="hidden" />
                                     </label>
                                  </div>
                                )}
                            </div>

                            {/* Right: Layout Settings Sidebar (25%) */}
                            <div className="w-80 bg-white border-l border-gray-100 p-6 flex flex-col gap-6 shrink-0 z-10 shadow-[0_0_20px_rgba(0,0,0,0.03)]">
                                <div>
                                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                                        <Layout className="w-3 h-3" />
                                        Layout Settings
                                    </h4>
                                    
                                    <div className="space-y-6">
                                        {/* Alignment Controls */}
                                        <div>
                                            <label className="text-sm font-medium text-gray-700 mb-2 block">Alignment</label>
                                            <div className="flex bg-gray-100 p-1 rounded-lg">
                                                <button 
                                                    onClick={() => setInsertLayout({ ...insertLayout, align: 'left' })}
                                                    className={`flex-1 py-1.5 rounded-md flex items-center justify-center transition-all ${insertLayout.align === 'left' ? 'bg-white shadow-sm text-amber-600' : 'text-gray-500 hover:text-gray-700'}`}
                                                    title="Left (Text wraps right)"
                                                >
                                                    <AlignLeft className="w-4 h-4" />
                                                </button>
                                                <button 
                                                    onClick={() => setInsertLayout({ ...insertLayout, align: 'center' })}
                                                    className={`flex-1 py-1.5 rounded-md flex items-center justify-center transition-all ${insertLayout.align === 'center' ? 'bg-white shadow-sm text-amber-600' : 'text-gray-500 hover:text-gray-700'}`}
                                                    title="Center (No wrap)"
                                                >
                                                    <AlignCenter className="w-4 h-4" />
                                                </button>
                                                <button 
                                                    onClick={() => setInsertLayout({ ...insertLayout, align: 'right' })}
                                                    className={`flex-1 py-1.5 rounded-md flex items-center justify-center transition-all ${insertLayout.align === 'right' ? 'bg-white shadow-sm text-amber-600' : 'text-gray-500 hover:text-gray-700'}`}
                                                    title="Right (Text wraps left)"
                                                >
                                                    <AlignRight className="w-4 h-4" />
                                                </button>
                                            </div>
                                            <p className="text-[10px] text-gray-400 mt-2">
                                                {insertLayout.align === 'left' && "Grid sits on left, text flows on right."}
                                                {insertLayout.align === 'center' && "Grid takes full line, text pushes down."}
                                                {insertLayout.align === 'right' && "Grid sits on right, text flows on left."}
                                            </p>
                                        </div>

                                        {/* Width Slider */}
                                        <div>
                                            <div className="flex justify-between mb-2">
                                                <label className="text-sm font-medium text-gray-700">Width</label>
                                                <span className="text-xs font-mono text-gray-500 bg-gray-100 px-1.5 rounded">{insertLayout.width}%</span>
                                            </div>
                                            <input 
                                                type="range" 
                                                min="20" 
                                                max="100" 
                                                step="5"
                                                value={insertLayout.width} 
                                                onChange={(e) => setInsertLayout({ ...insertLayout, width: parseInt(e.target.value) })}
                                                className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber-500"
                                            />
                                            <div className="flex justify-between mt-1 text-[10px] text-gray-400">
                                                <span>Small</span>
                                                <span>Half</span>
                                                <span>Full</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Insert Button */}
                                <div className="mt-auto pt-6 border-t border-gray-100">
                                    <button
                                        onClick={handleInsertContent}
                                        disabled={selectedFiles.length === 0 || (modalConfig.type === 'collage' && selectedFiles.length !== 4)}
                                        className={`w-full py-3 rounded-xl font-bold text-sm shadow-md flex items-center justify-center gap-2 transition-all
                                            ${selectedFiles.length === 0 || (modalConfig.type === 'collage' && selectedFiles.length !== 4)
                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed shadow-none'
                                            : 'bg-amber-500 hover:bg-amber-600 text-white hover:shadow-lg active:scale-95'}`}
                                    >
                                        {selectedFiles.length === 0 ? 'Select Images' : 'Insert Layout'}
                                    </button>
                                </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                </div>
              </>
            )}
          </div>

          {/* Sidebar - Article Settings */}
          <div className="lg:col-span-1 space-y-6">
            
            {/* 1. Basic Settings */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Article Settings</h3>
       
              <div className="space-y-4">
                {/* ----------------------------------------------------------------------- */}
              {/* AUTHOR SELECTION & MANAGEMENT SECTION                                   */}
              {/* ----------------------------------------------------------------------- */}
              <div>
                <div className="flex justify-between items-end mb-2">
                  <label className="block text-sm font-medium text-gray-700">Author *</label>
                  <div className="flex items-center gap-3">
                    {/* View All Button */}
                    <button 
                      onClick={() => setShowAuthorListModal(true)}
                      className="text-xs text-gray-500 hover:text-gray-900 font-medium flex items-center gap-1 transition-colors"
                      type="button"
                    >
                      <ListIcon className="w-3 h-3" /> View All
                    </button>
                    
                    {/* Divider */}
                    <div className="h-3 w-px bg-gray-300"></div>
                    
                    {/* Add New Button */}
                    <button 
                      onClick={() => openAuthorModal()}
                      className="text-xs text-amber-600 hover:text-amber-700 font-bold flex items-center gap-1 transition-colors"
                      type="button"
                    >
                      <Plus className="w-3 h-3" /> Add New
                    </button>
                  </div>
                </div>
                
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <select
                    value={article?.author_name || ''}
                    onChange={(e) => setArticle({ ...article, author_name: e.target.value })}
                    className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 appearance-none bg-white"
                  >
                    <option value="">Select an Author</option>
                    {authors.map(auth => (
                      <option key={auth.id} value={auth.display_name}>
                        {auth.display_name}
                      </option>
                    ))}
                  </select>
                  
                  {/* Quick Edit Button (Inside Input) */}
                  {article?.author_name && (
                       <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center">
                          {authors.find(a => a.display_name === article.author_name) && (
                              <button
                                  onClick={() => openAuthorModal(authors.find(a => a.display_name === article.author_name))}
                                  className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                  title="Edit this author"
                                  type="button"
                              >
                                  <Edit2 className="w-3.5 h-3.5" />
                              </button>
                          )}
                       </div>
                  )}
                </div>
                <p className="text-[10px] text-gray-400 mt-1">
                  Select from list or add a new author.
                </p>
              </div>

      {/* ----------------------------------------------------------------------- */}
      {/* MODAL 1: VIEW ALL AUTHORS LIST                                          */}
      {/* ----------------------------------------------------------------------- */}
      {showAuthorListModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[80vh]">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50 shrink-0">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Author List</h3>
                <p className="text-xs text-gray-500">Manage all registered authors</p>
              </div>
              <button onClick={() => setShowAuthorListModal(false)} className="text-gray-400 hover:text-gray-900 bg-white p-1 rounded-full shadow-sm border border-gray-200">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* List Content */}
            <div className="overflow-y-auto p-0 flex-1 custom-scrollbar">
              {authors.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <User className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                  <p>No authors found.</p>
                </div>
              ) : (
                <table className="w-full text-sm text-left">
                  <thead className="bg-gray-50 text-gray-500 font-medium border-b border-gray-100 sticky top-0 z-10">
                    <tr>
                      <th className="px-6 py-3">Name</th>
                      <th className="px-6 py-3">Email</th>
                      <th className="px-6 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {authors.map((auth) => (
                      <tr key={auth.id} className="hover:bg-gray-50 group transition-colors">
                        <td className="px-6 py-3">
                          <div className="font-medium text-gray-900">{auth.display_name}</div>
                          {auth.name && <div className="text-[10px] text-gray-400 font-mono">{auth.name}</div>}
                        </td>
                        <td className="px-6 py-3 text-gray-500">
                          {auth.email || <span className="text-gray-300 italic">No email</span>}
                        </td>
                        <td className="px-6 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {/* Edit Action */}
                            <button 
                              onClick={() => {
                                setShowAuthorListModal(false); // Close list modal
                                openAuthorModal(auth);         // Open edit modal
                              }}
                              className="p-1.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
                              title="Edit Author"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                            
                            {/* Delete Action */}
                            <button 
                              onClick={() => handleDeleteAuthor(auth.id)}
                              className="p-1.5 text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors"
                              title="Delete Author"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t border-gray-100 bg-gray-50 shrink-0 flex justify-between items-center">
               <span className="text-xs text-gray-500">Total: {authors.length} authors</span>
               <button 
                  onClick={() => {
                    setShowAuthorListModal(false);
                    openAuthorModal();
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors shadow-sm"
               >
                 <Plus className="w-4 h-4" /> Add New Author
               </button>
            </div>
          </div>
        </div>
      )}

      {/* ----------------------------------------------------------------------- */}
      {/* MODAL 2: ADD/EDIT AUTHOR FORM (Existing Modal)                          */}
      {/* ----------------------------------------------------------------------- */}
      {showAuthorModal && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in zoom-in duration-200">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <h3 className="text-lg font-bold text-gray-900">
                {isEditingAuthor ? 'Edit Author' : 'Add New Author'}
              </h3>
              <button onClick={() => setShowAuthorModal(false)} className="text-gray-400 hover:text-gray-900">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Display Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Name *</label>
                <input
                  type="text"
                  value={currentAuthor.display_name}
                  onChange={(e) => setCurrentAuthor({ ...currentAuthor, display_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="e.g. John Doe"
                />
              </div>

               {/* Email */}
               <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={currentAuthor.email}
                  onChange={(e) => setCurrentAuthor({ ...currentAuthor, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="john@example.com"
                />
              </div>

              {/* Bio */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Short Bio</label>
                <textarea
                  rows={3}
                  value={currentAuthor.bio}
                  onChange={(e) => setCurrentAuthor({ ...currentAuthor, bio: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => setShowAuthorModal(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAuthor}
                disabled={!currentAuthor.display_name}
                className="px-4 py-2 text-sm bg-amber-500 text-white font-medium rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                {isEditingAuthor ? 'Update Author' : 'Create Author'}
              </button>
            </div>
          </div>
        </div>
      )}



                {/* Photographer */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Photographer *</label>
                  <div className="relative">
                    <ImageIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={article?.photographer || ''}
                      onChange={(e) => setArticle({ ...article, photographer: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                      placeholder="Photographer name"
                    />
                  </div>
                </div>

                {/* Courtesy */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Courtesy</label>
                  <div className="relative">
                    <Star className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={article?.courtesy || ''}
                      onChange={(e) => setArticle({ ...article, courtesy: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                      placeholder="Courtesy name"
                    />
                  </div>
                </div>

                {/* Location */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Location *</label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={article?.location || ''}
                      onChange={(e) => setArticle({ ...article, location: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                      placeholder="Location"
                    />
                  </div>
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                  <select
                    value={article?.category || ''}
                    onChange={(e) => {
                      const cat = categories.find(c => c.name === e.target.value);
                      if (!cat) return;
                      setSelectedCategory(cat);
                      setArticle({ ...article, category: cat.name, subcategory: '' });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">Select category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.name}>{cat.display_name}</option>
                    ))}
                  </select>
                </div>

                {/* Subcategory */}
                {selectedCategory?.subcategories?.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subcategory</label>
                    <select
                      value={article?.subcategory || ''}
                      onChange={(e) => setArticle({ ...article, subcategory: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">Select subcategory</option>
                      {selectedCategory.subcategories.map(sub => (
                        <option key={sub} value={sub}>
                          {sub.charAt(0).toUpperCase() + sub.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Reading Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Reading Time (mins)</label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="number"
                      min="1"
                      max="60"
                      value={article?.reading_time || 5}
                      onChange={(e) => setArticle({ ...article, reading_time: parseInt(e.target.value) })}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                </div>

                {/* Hero Image */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Hero Image</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) setArticle({ ...article, hero_image: file });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                    {article?.hero_image && (
                      <div className="mt-2">
                        <img
                          src={article.hero_image instanceof File ? URL.createObjectURL(article.hero_image) : article.hero_image}
                          alt="Hero Preview"
                          className="h-32 object-cover rounded-lg w-full"
                        />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 2. Publishing Schedule */}
            <div className={`bg-white rounded-xl shadow-sm border p-6 ${isScheduled ? 'ring-2 ring-purple-500 border-transparent' : ''}`}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                        <Calendar className="w-5 h-5 mr-2 text-purple-600" />
                        Publishing Schedule
                    </h3>
                    <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                            type="checkbox" 
                            className="sr-only peer"
                            checked={isScheduled}
                            onChange={(e) => setIsScheduled(e.target.checked)}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                </div>

                {isScheduled && (
                    <div className="space-y-3 animate-fadeIn">
                        <div>
                            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Date</label>
                            <input
                                type="date"
                                min={new Date().toISOString().split('T')[0]}
                                value={scheduleDate}
                                onChange={(e) => {
                                    setScheduleDate(e.target.value);
                                }}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Hour</label>
                                <select
                                    value={scheduleHour}
                                    onChange={(e) => setScheduleHour(e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                                >
                                    <option value="">Hr</option>
                                    {getAvailableHours(scheduleDate).map((h) => (
                                        <option key={h} value={h}>{h}:00</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Minute</label>
                                <select
                                    value={scheduleMinute}
                                    onChange={(e) => setScheduleMinute(e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                                >
                                    <option value="">Min</option>
                                    {getAvailableMinutes(scheduleDate, scheduleHour).map((m) => (
                                        <option key={m} value={m}>{m}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="text-xs text-purple-600 bg-purple-50 p-2 rounded flex items-start">
                            <CheckCircle className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" />
                            Article will be scheduled after saving.
                        </div>
                    </div>
                )}
            </div>

            {/* 3. Article Flags */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Article Flags</h3>
              
              <div className="space-y-3">
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={article?.featured || false}
                    onChange={(e) => setArticle({ ...article, featured: e.target.checked })}
                    className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                  />
                  <div className="flex items-center space-x-2">
                    <Star className="w-4 h-4 text-yellow-500" />
                    <span className="text-sm font-medium text-gray-700">Featured Article</span>
                  </div>
                </label>

                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={article?.trending || false}
                    onChange={(e) => setArticle({ ...article, trending: e.target.checked })}
                    className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                  />
                  <div className="flex items-center space-x-2">
                    <TrendingUp className="w-4 h-4 text-green-500" />
                    <span className="text-sm font-medium text-gray-700">Trending Article</span>
                  </div>
                </label>

                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={article?.premium || false}
                    onChange={(e) => setArticle({ ...article, premium: e.target.checked })}
                    className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                  />
                  <div className="flex items-center space-x-2">
                    <Crown className="w-4 h-4 text-purple-500" />
                    <span className="text-sm font-medium text-gray-700">Premium Content</span>
                  </div>
                </label>
              </div>
            </div>

            {/* 4. Tags */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Tags</h3>
              
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {article?.tags?.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      <Tag className="w-3 h-3 mr-1" />
                      {tag}
                      <button
                        onClick={() => handleTagRemove(tag)}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>

                <input
                  type="text"
                  placeholder="Add tag and press Enter"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleTagAdd(e.target.value.trim());
                      e.target.value = '';
                    }
                  }}
                />
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminArticleEditorPage;